import React from 'react';
import {eye, corruptedFile} from 'assets/images/images';
import {
  TableLayout,
  Loader,
  PageLimit,
  Pagination,
  Filter,
  FilterButton,
  EmptyState,
} from 'components';
import {filterOutEmptyValues, generateQueryParams} from 'helpers';
import moment from 'moment';
import {shortenLargeNumbers} from 'helpers/shortenLargeNumbers';
import {getStatusTextAndStyles} from 'helpers/getStatusTextAndStyles';

function BaasTransactionTable({
  transactions = [],
  isLoading = false,
  customerId = null,
  onViewTransaction = () => {},
  EmptyState: CustomEmptyState = null,
  totalPages = 1,
  currentPage = 1,
  onPageChange = () => {},
  limit = 10,
  onLimitChange = () => {},
  totalItems = 0,
  openFilter = false,
  setOpenFilter = () => {},
  filters = {},
  setFilters = () => {},
  filterOptions = [],
  onResetFilter = () => {},
  navigate = null,
  searchText = '',
  setSearchText = null,
  className = '',
  showPagination = true,
  showFilter = true,
  onFilterStateChange = () => {},
  initialFilters = {},
}) {
  const handleFilter = (selectedOptions, shouldDownload = false) => {
    if (!navigate) return;

    const filteredOptions = filterOutEmptyValues(selectedOptions);
    const queryParams = generateQueryParams(filteredOptions);
    const {status} = filteredOptions;
    if (Array.isArray(status)) {
      filteredOptions.status = status.join(',');
    }
    let baseUrl = '';
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    if (shouldDownload) {
      filteredOptions.download = true;
      setFilters({
        ...filters,
        ...filteredOptions,
      });
    } else {
      navigate(baseUrl);
    }

    setFilters(filteredOptions);
    setOpenFilter(false);
  };

  function shortenText(text, maxLength = 0) {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 5)}......${text.slice(-3)}`;
  }

  const isNotFiltered =
    !isLoading && (!transactions || transactions.length === 0);
  const isFiltered =
    isNotFiltered &&
    (Object.values(filters).some(s => s) ||
      (searchText && searchText.length > 0));

  const DefaultEmptyState = () => {
    if (isFiltered) {
      return (
        <EmptyState
          height={45}
          noBtn
          heading="No Transaction History Found"
          body="Oops, it looks like there's no transaction history that matches the filters/search term you've selected. Please try adjusting your filters."
        />
      );
    }

    return (
      <EmptyState
        height={45}
        noBtn
        className="w-full"
        src={corruptedFile}
        heading="No Transaction History Found"
        body="Sorry, it appears there is no transaction history available for this company."
        maxWidth="max-w-[500px]"
      />
    );
  };

  return (
    <>
      {showFilter && (
        <Filter
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          handleFilter={handleFilter}
          resetUrl={onResetFilter}
          filterOptions={filterOptions}
          onFilterStateChange={onFilterStateChange}
        />
      )}

      <TableLayout negativeMargins className={`w-full ${className}`}>
        {isLoading ? (
          <Loader height={45} />
        ) : (
          <>
            <thead className="text-small font-semibold uppercase">
              <tr className="bg-white80">
                <td className="p-5 pl-12 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Name
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Date Created
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  amount
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Channel
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Type
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  ref id
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  status
                </td>
                <td className="p-5 pl-0"></td>
              </tr>
            </thead>
            <tbody className="text-[13px] text-body bg-white">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-4 text-center">
                    {CustomEmptyState ? (
                      <CustomEmptyState />
                    ) : (
                      <DefaultEmptyState />
                    )}
                  </td>
                </tr>
              ) : (
                transactions.map((transaction, index) => (
                  <tr
                    key={index}
                    className="text-left border-b bg-white border-grey60"
                  >
                    <td className="p-4 pl-12 whitespace-nowrap">
                      {transaction?.transaction_type === 'transfer'
                        ? transaction?.recipient_account_name
                        : transaction?.sender_account_name || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {(transaction?.date_created &&
                        moment(transaction?.date_created)?.format(
                          'DD MMM YYYY h:m',
                        )) ||
                        '-'}{' '}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {shortenLargeNumbers(
                        transaction?.transaction_amount || 0,
                        'NGN',
                        {startAt: 'B'},
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span className="uppercase   w-fit rounded-full px-2 py-1 h-5 flex items-center justify-center text-dojahBlue bg-[#EEF3FC] text-xs">
                        {transaction?.source || '-'}
                      </span>{' '}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span
                        className={`${
                          getStatusTextAndStyles(
                            transaction?.transaction_type === 'transfer' &&
                              transaction?.transaction_event_type?.includes(
                                'transaction_charge',
                              )
                              ? 'charge'
                              : transaction?.transaction_type?.toLowerCase(),
                          )?.solid
                        } uppercase   w-fit rounded-full px-2 py-1 h-5 flex items-center justify-center  text-xs`}
                      >
                        {transaction?.transaction_type === 'transfer' &&
                        !transaction?.transaction_event_type?.includes(
                          'transaction_charge',
                        )
                          ? 'Debit'
                          : transaction?.transaction_event_type?.includes(
                              'transaction_charge',
                            )
                          ? 'Charge'
                          : 'Credit' || '-'}
                      </span>{' '}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {shortenText(transaction?.transaction_id || '--', 21) ||
                        '-'}{' '}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span
                        className={` ${
                          getStatusTextAndStyles(
                            transaction?.transaction_status?.toLowerCase(),
                          )?.styles
                        } uppercase w-fit rounded-full px-2 py-1 h-5   flex items-center justify-center  text-xs`}
                      >
                        {
                          getStatusTextAndStyles(
                            transaction?.transaction_status?.toLowerCase(),
                          )?.text
                        }
                      </span>
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap flex items-center gap-4 justify-between">
                      <button onClick={() => onViewTransaction(transaction)}>
                        <img src={eye} alt="View" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </>
        )}
      </TableLayout>

      {showPagination && !isLoading && transactions.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:gap-0">
          <PageLimit
            onLimitChange={onLimitChange}
            limit={limit}
            total={totalItems}
            length={transactions.length}
          />

          <Pagination
            total={totalPages}
            current={currentPage}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
}

BaasTransactionTable.FilterButton = function BaasTransactionFilterButton({
  openFilter,
  setOpenFilter,
  className = '',
}) {
  return (
    <FilterButton
      openFilter={openFilter}
      setOpenFilter={setOpenFilter}
      className={`h-[48px] justify-center w-[130px] ${className}`}
    />
  );
};

export default BaasTransactionTable;
